<script>
    import Calendar from "./Calendar.svelte";
    import { toggleComments, commentPost, apiRequest, communicationError, apiFetchResource } from "../lib/api"
    import { displayComments, displayNames, timestamp2date } from "../lib/display"
    import { deepcopy } from "../lib/util";
    import { holidays as holiday_variants } from "../lib/translations.json"

    /** @import { AuthVars } from "../lib/api.js" */
    /** @import { Holiday } from "./types.js" */

    /** @type {{authVars: AuthVars}} */
    let { authVars } = $props();

    const formatDate = ts => {
        const date = new Date(ts * 1000);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    };

    /** @type {Object.<string, Holiday>} */
    let holidays = $state({});

    /** @type {Calendar} */
    let calendarElem = $state();

    /** @type {HTMLDialogElement} */
    let createHolidayDialog = $state();
    /** @type {Holiday} */
    let holidayInput = $state({});
    /** @type {string?} */
    let holidayId = $state();
    let cmdStr = $state("Créer");

    /** @type {HTMLDialogElement} */
    let validateDialog = $state();
    let validateHolidayId = $state("");

    /** @type {Object.<string, Object.<string, number>} */
    let summary = $state({});

    /** @type {{(h: Holiday) => boolean}} */
    const hashedHoliday = h => !h.validated;
    /** @type (id: str, h: Holiday) => string,*/
    const holidayColor = (_, h) => holiday_variants[h.variant].color;


    /** @param {number} startDate @param {number} endDate   */
    async function fetchHolidays(startDate, endDate) {
        holidays = await apiFetchResource(
            authVars.session,
            "holidays.list",
            {startDate: startDate / 1000, endDate: endDate / 1000},
            "Erreur chargement vacances"
        );
        calendarElem.populateCalendarEvents(holidays);
    }

    async function submitHoliday() {
        holidayInput.startDate = new Date(`${holidayInput.startDate_raw} 00:00:00`).getTime() / 1000;
        holidayInput.endDate = new Date(`${holidayInput.endDate_raw} 23:59:59`).getTime() / 1000;

        if (holidayId) {
            const resp = await apiRequest(
                authVars.session,
                "holidays.modify",
                {
                    id: holidayId,
                    data: holidayInput
                }
            )
            if (!resp.ok) communicationError("Erreur modification vacance");
            holidays[holidayId] = deepcopy(holidayInput);
        } else {
            let newId;
            if (holidayInput.forced) {
                newId = await apiFetchResource(
                    authVars.session,
                    "holidays.force",
                    {
                        target: holidayInput.forcedTo,
                        data: holidayInput
                    },
                    "Erreure création vacances"
                );
            } else {
                newId = await apiFetchResource(
                    authVars.session,
                    "holidays.create",
                    holidayInput,
                    "Erreure création vacances"
                );
            }
            holidays[newId] = await apiFetchResource(
                authVars.session,
                "holidays.get",
                {id: newId}
            );
        }

        calendarElem.populateCalendarEvents(holidays);
        createHolidayDialog.close();
    }

    /** @param {string} id  */
    async function deleteHoliday(id) {
        if (!confirm(`Supprimer "${holidays[id].title}"?`)) return;

        const resp = await apiRequest(authVars.session, "holidays.remove", {id: id});
        if (!resp.ok) communicationError("Erreur lors suppression de vacance");

        delete holidays[id];
        calendarElem.populateCalendarEvents(holidays);
    }

    /** @param {string} id @param {string} action   */
    async function validateHoliday(id, action) {
        await apiRequest(authVars.session, "holidays.validate", {id: id, action: action});
        calendarElem.clearSelected();
        validateHolidayId = "";
        if (action == "validate") {
            holidays[id] = await apiFetchResource(
                authVars.session,
                "holidays.get",
                {id: id}
            );
        } else {
            delete holidays[id];
        }
        calendarElem.populateCalendarEvents(holidays);
        validateDialog.close();
    }

    /** @param {Date} referenceDate  */
    function calculateSummary(referenceDate) {
        const monthStartTimestamp = new Date(`${referenceDate.getFullYear()}-${referenceDate.getMonth()+1}-01 00:00:00`).getTime() / 1000;
        const tmpd = new Date(monthStartTimestamp * 1000);
        tmpd.setMonth(tmpd.getMonth()+1);
        tmpd.setDate(0);
        tmpd.setHours(23, 59, 59);
        const monthEndTimestamp = new Date(tmpd.getTime()).getTime() / 1000;

        summary = {};
        const h_to_count = Object.values(holidays).filter(h => {
            const c1 = h.startDate >= monthStartTimestamp;
            const c2 = h.startDate <= monthEndTimestamp;
            const c3 = h.endDate   >= monthStartTimestamp;
            const c4 = h.endDate   <= monthEndTimestamp;
            return ((c1 && c2) || (c3 && c4)) && h.validated;
        });

        for (const h of h_to_count) {
            if ( !summary[h.author] ) 
                summary[h.author] = {};
            if ( !summary[h.author][h.variant] )
                summary[h.author][h.variant] = 0;
            summary[h.author][h.variant] += Math.ceil((Math.min(monthEndTimestamp, h.endDate) - Math.max(monthStartTimestamp, h.startDate)) / (24 * 3600 + 100));
        }
    }

    function sumSummary(s) {
        let sum = 0;
        for (const v of Object.keys(holiday_variants))
            if (s[v])
                sum += s[v];
        return sum
    }

</script>


<section id="tool-bar">
    <button onclick={() => {
        holidayInput = {};
        holidayId = undefined;
        cmdStr = "Créer";
        createHolidayDialog.style.setProperty("--background-base-color", `var(--blue)`);
        createHolidayDialog.showModal();
    }}>  </button>
    {#if authVars.user.privileges.includes("holidays.moderator")}
        <button onclick={() => {
            holidayInput = {forced: true};
            holidayId = undefined;
            cmdStr = "Imposer";
            createHolidayDialog.style.setProperty("--background-base-color", `var(--blue)`);
            createHolidayDialog.showModal();
        }}> 󰨱 </button>
    {/if}
</section>

<div class="app-display">
    <Calendar
        onscroll={fetchHolidays}
        hashedFilter={hashedHoliday}
        colorFilter={holidayColor}
        bind:this={calendarElem}
    />

    {#if calendarElem?.getSelectedDay()}
        <aside>
            <div class="header">
                <button onclick={calendarElem.clearSelected}>X</button>
                <h2 class="gradient box">{calendarElem.getSelectedDay().toLocaleDateString("fr", {dateStyle: "full"})}</h2>
            </div>
            {#if authVars.user.privileges.includes("holidays.moderator")}
                <button onclick={() => {
                    calculateSummary(calendarElem.getSelectedDay());
                    document.getElementById("summary-dialog")?.showModal();
                }}> 󰸗 Résumé Mensuel </button>
                <br/>
            {/if}
            <div class="body masonry-layout">
                {#each calendarElem.getSelectedEvents() as hid (hid)}
                    {@const holiday = holidays[hid]}
                    {@const dateStart = new Date(holiday.startDate * 1000).toLocaleDateString("fr")}
                    {@const dateEnd   = new Date(holiday.endDate   * 1000).toLocaleDateString("fr")}
                    <div class="gradient box" style="--background-base-color: {calendarElem.getEventColor(hid, holiday)};">
                        <h2>{holiday.title}</h2>
                        <div class="dark-bg details">
                            {#if dateStart == dateEnd}
                                Le <em>{dateStart}</em>
                            {:else}
                                Du <em>{dateStart}</em> au <em>{dateEnd}</em>
                            {/if}
                            <br/>
                            Posté par <em>{displayNames({...authVars.groups, ...authVars.names}, [holiday.author])}</em> le {new Date(holiday.creationDate * 1000).toLocaleDateString("fr")}
                            <br/>
                            󰚒 : <em>{holiday_variants[holiday.variant].name}</em>
                            <br/>
                             : {#if holiday.validated}
                                <em style="color: #55ff55;">Validé</em>
                            {:else}
                                <em>En Attente</em>
                            {/if}
                            {#if holiday.description}
                                <br/>
                                <br/>
                                <span style="color: var(--text);">{holiday.description}</span>
                            {/if}
                        </div>
                        <div class="flex-scroll dark-buttons">
                            {#if authVars.user.privileges.includes("holidays.moderator") || authVars.user.id == holiday.author}
                                <button class="dark-button" onclick={()=>{toggleComments(hid, holiday, authVars.session, "holidays")}}>󰅺 Commenter {displayComments(holiday.comments)}</button>
                                {#if !holiday.validated}
                                    <button
                                        onclick={()=>{
                                            holidayId = hid;
                                            holidayInput = deepcopy(holiday);
                                            holidayInput.startDate_raw = formatDate(holiday.startDate);
                                            holidayInput.endDate_raw = formatDate(holiday.endDate);
                                            console.log(holidayInput.startDate_raw);
                                            cmdStr = "Modifier";
                                            createHolidayDialog.style.setProperty("--background-base-color", calendarElem.getEventColor(hid));
                                            createHolidayDialog.showModal();
                                        }}
                                    >󰢧 Modifier</button>
                                {/if}
                            {/if}
                            {#if authVars.user.privileges.includes("holidays.moderator")}
                                {#if holiday.validated}
                                    <button onclick={()=>{deleteHoliday(hid)}}> Supprimer</button>
                                {:else}
                                    <button onclick={()=>{
                                        validateHolidayId = hid;
                                        validateDialog.style.setProperty("--background-base-color", calendarElem.getEventColor(hid));
                                        validateDialog.showModal();
                                    }}> Valider</button>
                                {/if}
                            {/if}
                        </div>
                        {#if holiday.commentsVisible}
                            {#if holiday.comments?.length}
                                <ul class="dark-bg comments">
                                    {#each holiday.comments as comment}
                                        <li>
                                            <small>{timestamp2date(comment.timestamp)}</small> <em>{authVars.names[comment.author]}</em> : {comment.comment}
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                            <form style="display: flex; width: 100%;" onsubmit={e=>{
                                e.preventDefault();
                                const f = new FormData(e.target);
                                commentPost(hid, holiday, f.get("comment"), authVars.session, "holidays");
                                e.target.reset();
                            }}>
                                <input type="text" name="comment" required placeholder="Commentaire" style="flex: 1; margin-right: .5em;" />
                                <button type="submit" class="dark-button"></button>
                            </form>
                        {/if}
                    </div>
                {/each}
            </div>
        </aside>
    {/if}
</div>

<dialog bind:this={createHolidayDialog} id="event-create-dialog" class="gradient" closedby="any">
    <h1>{cmdStr} des Vacances</h1>

    <form id="event-form" onsubmit={e=>{e.preventDefault(); submitHoliday()}}>
        <!-- <input -->
        <!--     type="text" -->
        <!--     bind:value={holidayInput.title} -->
        <!--     required -->
        <!--     maxlength="20" -->
        <!--     placeholder="Titre" -->
        <!--     style="font-weight: bold; font-size: 110%;" -->
        <!-- /> -->

        <select
            required
            style="margin: .5em;"
            onchange={()=>{createHolidayDialog.style.setProperty("--background-base-color", `${holiday_variants[holidayInput.variant].color}`);}}
            bind:value={holidayInput.variant}
        >
            <option value={undefined} disabled selected>Type</option>
            {#each Object.entries(holiday_variants) as [id, var_data] }
                <option value={id}>{var_data.name}</option>
            {/each}
        </select>

        {#if holidayInput.forced}
            <select
                required
                style="margin: .5em;"
                bind:value={holidayInput.forcedTo}
            >
                <option value={undefined} disabled selected>Utilisateur</option>
                {#each Object.entries(authVars.names) as [id, name] }
                    <option value={id}>{name}</option>
                {/each}
            </select>
        {/if}

        <div style="margin-bottom: .5em;">
            <label style="display: inline-flex; align-items: center;">
                Du : 
                <input
                    type="date"
                    required
                    bind:value={holidayInput.startDate_raw}
                    max="{holidayInput.endDate_raw}"
                />
            </label>
            <label style="display: inline-flex; align-items: center;">
                Au : 
                <input
                    type="date"
                    required
                    bind:value={holidayInput.endDate_raw}
                    min="{holidayInput.startDate_raw}"
                />
            </label>
        </div>

        <textarea bind:value={holidayInput.description} placeholder="Description" class="description" style="height: 10lh;"></textarea>
    </form>

    <button class="dark-button" type="submit" form="event-form">{cmdStr} &thinsp;󰬤</button>
    <button class="dark-button" command="close" commandfor="event-create-dialog">Fermer </button>
</dialog>

<dialog bind:this={validateDialog} id="validate-dialog" class="gradient" closedby="any">
    {#if validateHolidayId}
        {@const holiday = holidays[validateHolidayId]}
        {@const dateStart = new Date(holiday.startDate * 1000).toLocaleDateString("fr")}
        {@const dateEnd   = new Date(holiday.endDate   * 1000).toLocaleDateString("fr")}
        <h1>Valider les Vacances de {authVars.names[holiday.author]}?</h1>

        <div class="dark-bg details">
            <em>{holiday.title}</em>
            <br/>
            <br/>
            {#if dateStart == dateEnd}
                Le <em>{dateStart}</em>
            {:else}
                Du <em>{dateStart}</em> au <em>{dateEnd}</em>
            {/if}
            <br/>
            Posté le {new Date(holiday.creationDate * 1000).toLocaleDateString("fr")}
            <br/>
            󰚒 : <em>{holiday_variants[holiday.variant].name}</em>
            {#if holiday.description}
                <br/>
                <br/>
                <span style="color: var(--text);">{holiday.description}</span>
            {/if}
        </div>

        <button
            class="dark-button valiate-button"
            style="color: red;"
            onclick={()=>{ validateHoliday(validateHolidayId, "reject"); }}
        >  </button>
        <button
            class="dark-button"
            command="close"
            commandfor="validate-dialog"
            style="margin: auto 1em; vertical-align: center;"
        > Fermer  </button>
        <button
            class="dark-button valiate-button"
            style="color: lime;"
            onclick={()=>{ validateHoliday(validateHolidayId, "validate"); }}
        >  </button>
    {/if}
</dialog>

<dialog id="summary-dialog" class="gradient" closedby="any">
    <h1>Résumé Mensuel</h1>
    <ul id="summary-content" class="dark-bg" style="text-align: left;">
        {#each Object.entries(summary) as [id, summarized_user]}
            <li>
                <em>{authVars.names[id]}</em>
                <ul>
                    {#each Object.entries(holiday_variants) as [vid, vartand_data]}
                        {#if summarized_user[vid]}
                            <li> <em style="color: {vartand_data.color};">{vartand_data.name}</em> : {summarized_user[vid]}j </li>
                        {/if}
                    {/each}
                    <li><em style="color: var(--text);">Somme</em> : {sumSummary(summarized_user)}j</li>
                </ul>
            </li>
        {/each}
    </ul>
    <button
        class="dark-button"
        onclick={()=>{
            const summaryElem = document.getElementById("summary-content");
            navigator.clipboard.writeText(summaryElem.textContent);
            alert("Résumé Copié!");
        }}
    > Copier  </button>
    <button
        class="dark-button"
        command="close"
        commandfor="summary-dialog"
        style="margin: auto 1em; vertical-align: center;"
    > Fermer  </button>
</dialog>

<style>
    .valiate-button {
        margin: .2em;
        padding: .2em;
        font-size: 400%;
    }
</style>
